import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppContext } from '@prisjakt/ui-app-context';
import {
  CaptionText,
  H3Text,
  FontScale,
  FontWeight,
} from '@prisjakt/ui/text/variants';
import { Price } from 'src/types';
import { defaultProductState } from 'src/areas/product/reducer';
import PriceInformation from '../../price-information';
import {
  getSortedPrices,
  getAllOffersAsArray,
} from 'src/components/price-list/sorting';
import PriceListHeader from './price-list-header';
import PriceListFooter from './price-list-footer';
import PriceListBody from './price-list-body';
import { IPriceListPanelProps } from './types';
import { getCommonListProps } from './utils';
import { getSplitOfferlist, getListLimits } from './utils/split-list';
import MissingShop from '../../missing-shop';
import OfferListEmptyMessage from '../offer-list-empty-message';
import {
  FilterTypeEnum,
  PriceDisplayEnum,
  SortOptionEnum,
} from 'src/components/price-list/types';
import { AppState } from 'src/reducers';
import { PriceListFilterContext } from 'src/areas/product/price-list-filter-context';
import { PricePanel, PartnerOffersTitle, HeaderWrapper } from './styles';
import { filterOffersBySelections } from 'src/components/price-list/filtering';

export const PriceListPanel: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IPriceListPanelProps>
>> = props => {
  const {
    me,
    prices,
    variantGroups,
    priceListStatistics,
    priceDisplay,
    currentSorting,
    category,
    showProductInformationTexts,
    storeStatistics,
  } = props;
  const { market, t } = useAppContext();
  const { selectedSizeVariants } = useContext(PriceListFilterContext);
  const [showMorePrices, setShowMorePrices] = useState(false);
  const [showMoreInSecondaryList, setShowMoreInSecondaryList] = useState(false);

  const filterOffers = (offers: Price[]) => {
    return filterOffersBySelections(offers, [
      {
        type: FilterTypeEnum.VARIANT_SIZE,
        selection: selectedSizeVariants ?? [],
      },
    ]);
  };

  const getFilteredAndSortedOffers = () => {
    const allOffers = getAllOffersAsArray(prices);
    const filteredOffers = filterOffers(allOffers);
    const sortedPrices = getSortedPrices(
      priceDisplay,
      currentSorting,
      filteredOffers
    );
    return getSplitOfferlist(
      sortedPrices,
      currentSorting,
      priceDisplay === PriceDisplayEnum.PRICE_INCLUDES_SHIPPING,
      Boolean(selectedSizeVariants?.length)
    );
  };

  const offers = getFilteredAndSortedOffers();
  const hasOffers =
    Boolean(offers.mainList.length) || Boolean(offers.secondaryList?.length);
  const commonListProps = getCommonListProps(props);
  const listLimits = getListLimits(offers);
  const currentSort =
    useSelector((state: AppState) => getSortState(state)) ||
    defaultProductState.sorting;
  const secondaryListHeaderText = `${t('product', 'splitlist-other.title')} 
    (${offers.secondaryList.length})`;

  // Default sorting in UK/FR is Partner offers
  const showSortByFeaturedHeader =
    currentSort === SortOptionEnum.FEATURED &&
    ['fr', 'gb'].includes(market.code);

  return (
    <PricePanel>
      <PriceListHeader
        priceListStatistics={priceListStatistics}
        variantGroups={variantGroups}
        categoryId={category?.id}
      />
      {me?.analyticsAccess?.stores && <MissingShop />}

      {showSortByFeaturedHeader && (
        <PartnerOffersTitle>
          {t('product', 'price-list.header-partner-offers')}
        </PartnerOffersTitle>
      )}

      {hasOffers && (
        <>
          {offers.mainList.length ? (
            <PriceListBody
              isPrimaryList
              sortedPrices={offers.mainList}
              showMorePrices={showMorePrices}
              numberOfVisiblePrices={listLimits.mainListLimit}
              {...commonListProps}
            />
          ) : (
            <OfferListEmptyMessage />
          )}
          <PriceListFooter
            showProductInformationTexts={showProductInformationTexts}
            sortedPrices={offers.mainList}
            showMorePrices={showMorePrices}
            numberOfVisiblePrices={listLimits.mainListLimit}
            setShowMorePrices={setShowMorePrices}
          />
          {Boolean(offers.secondaryList.length) && (
            <>
              <HeaderWrapper>
                <H3Text>{secondaryListHeaderText}</H3Text>
                <CaptionText scale={FontScale.S} weight={FontWeight.HEAVIER}>
                  {t('product', 'splitlist-other.description')}
                </CaptionText>
              </HeaderWrapper>
              <PriceListBody
                isPrimaryList={false}
                sortedPrices={offers!.secondaryList!}
                showMorePrices={showMoreInSecondaryList}
                numberOfVisiblePrices={listLimits.secondaryListLimit}
                {...commonListProps}
              />
              <PriceListFooter
                showProductInformationTexts={showProductInformationTexts}
                sortedPrices={offers.secondaryList!}
                showMorePrices={showMoreInSecondaryList}
                numberOfVisiblePrices={listLimits.secondaryListLimit}
                setShowMorePrices={setShowMoreInSecondaryList}
              />
            </>
          )}
        </>
      )}
      {storeStatistics && (
        <PriceInformation storeStatistics={storeStatistics} />
      )}
    </PricePanel>
  );
};

const getSortState = (state: AppState) => state.areas.product.sorting;
